import './styles.scss'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import Loader from 'Components/Loader'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import DialogUIModal from 'Components/UIComponents/DialogUIModal'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Validation from 'Utils/Validation'

const initialFormData = {
  countryId: '',
  year: '',
  totalProduction: '',
  arabica: '',
  robusta: '',
  exports: '',
  consumption: ''
}

const requiredFields = ['countryId', 'year', 'totalProduction', 'arabica', 'robusta', 'exports', 'consumption']

const AddCountryWiseProductionReport = ({ setLoading, showForm, setShowForm, onSuccess, countryOptions = [] }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState(initialFormData)
  const [errors, setErrors] = useState({})
  const [openModal, setOpenModal] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {} })

  const handleDismiss = () => {
    setOpenModal({ ...openModal, showModal: false })
  }

  const closeDialogModal = () => {
    setShowForm(false)
    setFormData(initialFormData)
    setErrors({})
  }

  const handleInputChange = (value, field) => {
    if (Validation.numberDotPlusAndMinusValidation(value) || value === '') {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }))
    }

    setErrors((prev) => ({
      ...prev,
      [field]: false
    }))
  }

  const handleCountryChange = (event) => {
    const selectedCountryId = event.target.value

    setFormData((prev) => ({
      ...prev,
      countryId: selectedCountryId ?? ''
    }))

    setErrors((prev) => ({
      ...prev,
      countryId: false
    }))
  }

  const handleBlur = (field) => {
    if (!String(formData[field]).trim()) {
      setErrors((prev) => ({
        ...prev,
        [field]: true
      }))
    }
  }

  const isSubmitDisabled = requiredFields.some((field) => !String(formData[field]).trim())

  const getPayload = () => ({
    countryId: Number(formData.countryId),
    year: String(formData.year),
    totalProduction: Number(formData.totalProduction),
    arabica: Number(formData.arabica),
    robusta: Number(formData.robusta),
    exports: Number(formData.exports),
    consumption: Number(formData.consumption)
  })

  const insertCountryWiseProductionReport = async () => {
    setLoading(true)
    handleDismiss()

    try {
      const response = await apiAdapterCoffeeWeb.postCountryWiseProductionReport(getPayload())

      if (response?.data?.statusCode === 200 || response?.status === 200) {
        setOpenModal({
          ...openModal,
          showModal: true,
          modalType: 'success',
          message: t('REPORT_DATA_ADDED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleDismiss
        })
        setShowForm(false)
        setFormData(initialFormData)
        onSuccess?.()
      } else {
        setOpenModal({
          ...openModal,
          showModal: true,
          modalType: 'error',
          message: t('SORRY_COULD_NOT_ADD_DATA_PLEASE_TRY_AGAIN_LATER'),
          onRetry: insertCountryWiseProductionReport,
          onHide: handleDismiss
        })
      }
    } catch {
      setOpenModal({
        ...openModal,
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_ADD_DATA_PLEASE_TRY_AGAIN_LATER'),
        onRetry: insertCountryWiseProductionReport,
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const renderInput = (field, label) => (
    <div className="form-group" key={field}>
      <span className="p-float-label">
        <InputText value={formData[field]} onChange={(event) => handleInputChange(event.target.value, field)} onBlur={() => handleBlur(field)} className={`input-fields ${errors[field] ? 'error' : ''}`} placeholder={field === 'year' && 'E.g. 2026'} />
        <label className={`field-label ${errors[field] ? 'error-label' : ''}`}>
          {label}
          <span className="required-text-field">*</span>
        </label>
      </span>
      {errors[field] && <small className="error-message">{`${label} ${t('IS_REQUIRED')}`}</small>}
    </div>
  )

  const renderFooter = () => (
    <div className="add-country-production-footer">
      <ButtonComponent type={'cancel'} onClick={closeDialogModal} />
      <ButtonComponent type={'submit'} disabled={isSubmitDisabled} onClick={insertCountryWiseProductionReport} />
    </div>
  )

  const renderForm = () => (
    <div className="production-country-dynamic-add-form">
      <div className="form-group">
        <span className="p-float-label">
          <Dropdown inputId="add-production-country-id" value={formData.countryId ?? null} options={countryOptions} optionLabel="nicename" optionValue="id" onChange={handleCountryChange} placeholder={t('SELECT_COUNTRY')} className={`input-fields ${errors.countryId ? 'error' : ''}`} onBlur={() => handleBlur('countryId')} />
          <label htmlFor="add-production-country-id" className={`field-label ${errors.countryId ? 'error-label' : ''}`}>
            {t('COUNTRY_NAME')}
            <span className="required-text-field">*</span>
          </label>
        </span>
        {errors.countryId && <small className="error-message">{`${t('COUNTRY_NAME')} ${t('IS_REQUIRED')}`}</small>}
      </div>
      {renderInput('year', 'Year')}
      {renderInput('totalProduction', 'Total Production')}
      {renderInput('arabica', 'Arabica')}
      {renderInput('robusta', 'Robusta')}
      {renderInput('exports', 'Exports')}
      {renderInput('consumption', 'Consumption')}
      <div className="form-note">{t('NOTE_MILLIONS_BAGS')}</div>
    </div>
  )

  return (
    <>
      <DialogUIModal visible={showForm} onHide={closeDialogModal} header={t('ADD_COUNTRY_WISE_PRODUCTION_REPORT')} footer={renderFooter} body={renderForm} className="country-wise-production-add-modal" />
      <DialogModal {...openModal} onDismiss={handleDismiss} />
    </>
  )
}

export default Loader(AddCountryWiseProductionReport)
